import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Battery, ChevronRight, Zap, Shield, Sparkles, Leaf, Phone,
  ClipboardList, CheckCircle, Wrench, Boxes, Cpu, Gauge, Plug, Layers,
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import SEO from '../components/SEO';
import {
  DEYE_INVERTERS, DEYE_PACKAGES, DEYE_RANGE_IMG, DEYE_PDU, DEYE_SMART_METER,
  startingPrice, fmtAUD,
} from '../data/deyePackages';

const FAQS = [
  { q: 'What is included in a Deye battery package?', a: 'Each standard package includes one Deye hybrid inverter, one PDU base unit, Deye AI-W5.1-B battery modules (5.12 kWh each), and a smart meter — fully supplied and installed.' },
  { q: 'How many batteries can be included in one Deye stack?', a: 'One stack can include up to 6 Deye AI-W5.1-B battery modules, for a maximum of 30.72 kWh.' },
  { q: 'What battery sizes are available?', a: 'Package sizes range from 5.12 kWh (1 module) up to 30.72 kWh (6 modules).' },
  { q: 'Do I need a single-phase or three-phase inverter?', a: "This depends on your property's electrical supply and overall system requirements. Our team will confirm the right option for your home." },
  { q: 'Is the price final?', a: 'The advertised price is based on standard installation conditions. Additional costs may apply depending on site requirements, confirmed after the site inspection.' },
  { q: 'What does the site inspection check?', a: 'The switchboard, battery location, cable run, wall surface, base/slab suitability, and any additional installation requirements.' },
  { q: 'What happens if additional costs are required?', a: 'They will be confirmed after the site inspection and before installation is organised.' },
  { q: 'Is the BSTC rebate included in the price?', a: 'The shown out-of-pocket price assumes the customer assigns the applicable BSTC rebate to Supa Energy Hub.' },
];

const STACK_PARTS = [
  { icon: Cpu, t: 'Deye hybrid inverter', d: 'Converts and manages power between your solar, battery, home and the grid. Single-phase or three-phase options.' },
  { icon: Layers, t: 'PDU base unit', d: 'The base unit that connects and manages the battery stack with the inverter. PDU1 for single-phase, PDU3 for three-phase systems.' },
  { icon: Battery, t: '1–6 × AI-W5.1-B modules', d: 'Stackable 5.12 kWh LiFePO4 battery modules. Add modules to scale storage up to 30.72 kWh.' },
  { icon: Gauge, t: 'Deye smart meter', d: 'Measures your home energy flow so the system charges and discharges intelligently. SDM120 for single-phase, SDM630 for three-phase.' },
];

const PackageGrid = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="deye-grid">
    {DEYE_PACKAGES.map((p) => (
      <Link
        key={p.slug}
        to={`/${p.slug}`}
        className="group bg-white rounded-2xl border-2 border-[#B5D43B]/20 hover:border-[#B5D43B] hover:shadow-xl transition-all overflow-hidden flex flex-col"
        data-testid={`deye-card-${p.slug}`}
      >
        <div className="bg-gradient-to-br from-[#3D5A47] to-[#1a2820] p-5 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, #B5D43B 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
          <div className="relative">
            <div className="flex items-center gap-2 mb-1">
              <Battery className="w-4 h-4 text-[#B5D43B]" />
              <span className="text-[10px] uppercase tracking-widest text-[#B5D43B] font-bold">Deye AI-W5.1 · {p.position}</span>
            </div>
            <p className="text-4xl font-black leading-none">{p.kwh.toFixed(2)}<span className="text-lg text-white/60"> kWh</span></p>
            <p className="text-xs text-white/70 mt-2">{p.modules} × AI-W5.1-B module{p.modules > 1 ? 's' : ''}</p>
          </div>
        </div>
        <div className="p-5 flex flex-col flex-1">
          <p className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">Starting from</p>
          <p className="text-3xl font-black text-[#3D5A47] leading-tight mt-0.5">{fmtAUD(startingPrice(p))}</p>
          <p className="text-[11px] text-gray-500 mt-1">installed after estimated BSTC discount*</p>
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-xs">
            <span className="inline-flex items-center gap-1 text-gray-600"><Zap className="w-3 h-3" /> 4 inverter options</span>
            <span className="inline-flex items-center gap-1 text-[#3D5A47] font-semibold group-hover:text-[#B5D43B]">View <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
          </div>
        </div>
      </Link>
    ))}
  </div>
);

const DeyePackagesPage = () => (
  <div className="min-h-screen">
    <SEO
      title="Deye AI-W5.1 Battery Packages — Fully Installed"
      description="Deye AI-W5.1 home battery packages from 5.12 kWh to 30.72 kWh — fully supplied and installed by Supa Energy Hub. Single & three-phase Deye hybrid inverter options, transparent out-of-pocket pricing after estimated BSTC discount."
      jsonLd={[
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQS.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
        },
      ]}
    />
    <Header />
    <main>
      {/* HERO */}
      <section className="pt-28 pb-12 sm:pb-16 relative overflow-hidden bg-gradient-to-br from-[#f6f8f3] via-white to-[#eaf2dc]">
        <div className="absolute inset-0 opacity-[0.15] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, #3D5A47 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        <div className="absolute top-20 right-0 w-72 h-72 bg-[#B5D43B]/20 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6" data-testid="deye-main-breadcrumb">
            <Link to="/" className="hover:text-[#3D5A47]">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to="/packages" className="hover:text-[#3D5A47]">Packages</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-[#3D5A47] font-medium">Deye Battery Packages</span>
          </nav>
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 bg-[#B5D43B]/15 border border-[#B5D43B]/40 rounded-full px-3 py-1 mb-4">
                <Sparkles className="w-3.5 h-3.5 text-[#3D5A47]" />
                <span className="text-[11px] font-bold tracking-wider uppercase text-[#3D5A47]">Deye AI-W5.1 · Fully Supplied + Installed</span>
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#3D5A47] leading-[1.1] tracking-tight">
                Deye <span className="text-[#B5D43B] bg-[#3D5A47] px-2 rounded-md">AI-W5.1</span> Battery Packages
              </h1>
              <p className="text-base sm:text-lg text-gray-700 mt-5 max-w-2xl leading-relaxed">
                Store your daytime solar and power your home into the evening with the Deye AI-W5.1 all-in-one battery system.
                Choose a package from <strong>5.12 kWh up to 30.72 kWh</strong> — fully supplied, installed and rebate-handled,
                at one transparent out-of-pocket price.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <a href="#packages">
                  <Button className="bg-[#3D5A47] hover:bg-[#2a4032] text-white gap-2 text-sm sm:text-base font-semibold px-5 py-5 rounded-xl shadow-lg shadow-[#3D5A47]/20" data-testid="deye-main-cta">
                    Compare Deye Battery Packages <ArrowRight className="w-4 h-4" />
                  </Button>
                </a>
                <Link to="/get-estimate?source=deye-main">
                  <Button variant="outline" className="bg-white border-[#3D5A47]/20 text-[#3D5A47] hover:bg-[#f8faf5] gap-2 text-sm sm:text-base px-5 py-5 rounded-xl">
                    <ClipboardList className="w-4 h-4" /> Get My Deye Battery Quote
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="bg-gradient-to-br from-[#1a2820] to-[#3D5A47] rounded-2xl shadow-xl overflow-hidden">
                <img src={DEYE_RANGE_IMG} alt="Deye AI-W5.1 battery system range — scalable from 5.12 kWh to 30.72 kWh" className="w-full h-auto object-contain" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT DEYE */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">The brand</span>
            <h2 className="heading-2 text-[#3D5A47] mb-4">Why choose Deye batteries?</h2>
            <p className="text-gray-700 text-sm leading-relaxed mb-4">
              Deye is one of the world&rsquo;s leading hybrid inverter and battery storage manufacturers, with a strong global
              installed base and a growing presence in Australia. Their residential energy storage systems are designed around
              reliable, safe LiFePO4 chemistry and smart energy management — helping your home use more of its own solar, day and night.
            </p>
            <p className="text-gray-700 text-sm leading-relaxed">
              The <strong>Deye AI-W5.1</strong> range is an all-in-one residential storage solution that pairs a Deye hybrid
              inverter with stackable AI-W5.1-B battery modules in one neat, expandable system — ideal for homes that want
              to store more solar energy.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { icon: Boxes, t: 'Modular & stackable', d: 'Start with one module and add more — up to 6 per stack.' },
              { icon: Leaf, t: 'Safe LFP chemistry', d: 'AI-W5.1-B modules use LiFePO4 — stable and long-lasting.' },
              { icon: Zap, t: '1-phase & 3-phase', d: 'Inverter options to suit single- and three-phase homes.' },
              { icon: Shield, t: 'Store more solar', d: 'Expandable up to 30.72 kWh to cover higher evening usage.' },
            ].map((f, i) => (
              <div key={i} className="bg-[#f8faf5] rounded-xl p-5 border border-gray-100">
                <div className="w-10 h-10 rounded-lg bg-[#B5D43B]/15 flex items-center justify-center mb-3"><f.icon className="w-5 h-5 text-[#3D5A47]" /></div>
                <h3 className="font-bold text-sm text-[#3D5A47] mb-1">{f.t}</h3>
                <p className="text-xs text-gray-600">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW THE STACK WORKS */}
      <section className="py-16 bg-[#f8faf5] border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">Deye AI-W5.1 system</span>
          <h2 className="heading-2 text-[#3D5A47] mb-3">How the stackable battery system works</h2>
          <p className="text-gray-700 text-sm max-w-3xl leading-relaxed mb-8">
            Every Deye AI-W5.1 stack is built from four key parts. You start with one battery module and stack more on top to
            reach the capacity your home needs — in clean, predictable 5.12 kWh steps, up to 6 modules.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="deye-stack-parts">
            {STACK_PARTS.map((s, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                <div className="w-11 h-11 rounded-xl bg-[#3D5A47] flex items-center justify-center mb-3"><s.icon className="w-5 h-5 text-[#B5D43B]" /></div>
                <h3 className="font-bold text-sm text-[#3D5A47] mb-1">{s.t}</h3>
                <p className="text-xs text-gray-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>

          {/* PDU + smart meter by phase */}
          <div className="grid md:grid-cols-2 gap-4 mt-6">
            <div className="bg-white rounded-xl p-5 border border-gray-100" data-testid="deye-pdu-info">
              <div className="flex items-center gap-2 mb-3"><Layers className="w-5 h-5 text-[#3D5A47]" /><h3 className="font-bold text-sm text-[#3D5A47]">PDU base unit</h3></div>
              <p className="text-xs text-gray-600 mb-3">The PDU base is the base unit that connects and manages the battery stack with the inverter. The correct base is chosen for your system type.</p>
              <ul className="text-xs text-gray-700 space-y-1.5">
                <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-[#B5D43B] mt-0.5 flex-shrink-0" /><span><strong>{DEYE_PDU[1].name}</strong> — {DEYE_PDU[1].usedFor}</span></li>
                <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-[#B5D43B] mt-0.5 flex-shrink-0" /><span><strong>{DEYE_PDU[3].name}</strong> — {DEYE_PDU[3].usedFor}</span></li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-5 border border-gray-100" data-testid="deye-meter-info">
              <div className="flex items-center gap-2 mb-3"><Gauge className="w-5 h-5 text-[#3D5A47]" /><h3 className="font-bold text-sm text-[#3D5A47]">Smart meter</h3></div>
              <p className="text-xs text-gray-600 mb-3">Each package includes the correct Deye smart meter based on your phase type, so the system measures your home&rsquo;s energy flow accurately.</p>
              <ul className="text-xs text-gray-700 space-y-1.5">
                <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-[#B5D43B] mt-0.5 flex-shrink-0" /><span><strong>{DEYE_SMART_METER[1].name}</strong> — {DEYE_SMART_METER[1].usedFor}</span></li>
                <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-[#B5D43B] mt-0.5 flex-shrink-0" /><span><strong>{DEYE_SMART_METER[3].name}</strong> — {DEYE_SMART_METER[3].usedFor}</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* INVERTER OPTIONS (NO PRICES) */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">Inverter options</span>
          <h2 className="heading-2 text-[#3D5A47] mb-3">Deye AI-W5.1 hybrid inverter range</h2>
          <p className="text-gray-700 text-sm max-w-3xl leading-relaxed mb-6">
            Every package pairs your Deye AI-W5.1-B battery with a Deye hybrid inverter. Choose a single-phase or three-phase
            inverter to suit your property&rsquo;s electrical supply and solar size. Pricing for each option is shown on the
            individual package pages.
          </p>
          <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm" data-testid="deye-inverter-options-table">
            <table className="w-full text-sm">
              <thead className="bg-[#3D5A47] text-white text-left">
                <tr>
                  <th className="px-4 sm:px-6 py-3 font-semibold">Inverter Option</th>
                  <th className="px-4 sm:px-6 py-3 font-semibold">Model Number</th>
                  <th className="px-4 sm:px-6 py-3 font-semibold">Phase</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {DEYE_INVERTERS.map((inv) => (
                  <tr key={inv.id}>
                    <td className="px-4 sm:px-6 py-3.5">
                      <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-[#3D5A47]" /><span className="font-semibold text-[#3D5A47]">{inv.short}</span></div>
                    </td>
                    <td className="px-4 sm:px-6 py-3.5 text-gray-600 font-mono text-xs">{inv.model}</td>
                    <td className="px-4 sm:px-6 py-3.5 text-gray-600">{inv.phase === 3 ? '3 Phase' : '1 Phase'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section id="packages" className="py-16 bg-[#f8faf5] border-y border-gray-100 scroll-mt-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">Battery package options</span>
          <h2 className="heading-2 text-[#3D5A47] mb-2">Deye AI-W5.1 packages up to 30.72 kWh</h2>
          <p className="text-gray-700 text-sm max-w-3xl mb-8">
            Every price below is your installed, out-of-pocket cost after the estimated BSTC discount. Tap a package to see
            full inverter pricing, inclusions and the site inspection process.
          </p>
          <PackageGrid />
          <p className="text-xs text-gray-500 mt-6">
            *Prices assume standard installation and customer assignment of the applicable BSTC rebate to Supa Energy Hub. Additional site-specific costs may apply.
          </p>
        </div>
      </section>

      {/* STANDARD INSTALL + ADDITIONAL COSTS + SITE INSPECTION */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <div className="bg-[#f8faf5] rounded-2xl border border-gray-100 p-6 sm:p-7">
            <h2 className="heading-2 text-[#3D5A47] mb-2 flex items-center gap-2"><CheckCircle className="w-5 h-5 text-[#B5D43B]" /> Standard installation</h2>
            <p className="text-gray-700 text-sm leading-relaxed">
              Prices are based on standard installation conditions and customer assignment of the applicable BSTC rebate to
              Supa Energy Hub. Final price may vary depending on site conditions, switchboard requirements, battery location,
              cable run, wall surface, base/slab condition, safety requirements, and other installation factors.
            </p>
          </div>
          <div className="bg-[#fdf4ec] rounded-2xl border border-orange-100 p-6 sm:p-7">
            <h2 className="heading-2 text-[#3D5A47] mb-2 flex items-center gap-2"><Wrench className="w-5 h-5 text-orange-600" /> Additional costs that may apply</h2>
            <p className="text-gray-700 text-sm leading-relaxed">
              <strong>Important Note:</strong> Additional costs may apply if required, including bollards at $200 per site,
              awning at $200 per site, additional cabling beyond 5 m at $25 per metre, concrete slab/base at $150, fireproof
              backing/cement sheet at $150, additional switchboard works, obstructed cabling pathway, or other site-specific
              labour and materials. These will be confirmed after the site inspection before installation is organised.
            </p>
          </div>
          <div className="bg-[#f8faf5] rounded-2xl border border-gray-100 p-6 sm:p-7">
            <h2 className="heading-2 text-[#3D5A47] mb-2 flex items-center gap-2"><ClipboardList className="w-5 h-5 text-[#3D5A47]" /> Site inspection deposit</h2>
            <p className="text-gray-700 text-sm leading-relaxed">
              Non-refundable deposit for a technician site inspection to confirm installation suitability and identify any
              additional costs, if required. If you proceed, this amount is deducted from your final balance payable. If you
              cancel after the inspection or after receiving any additional cost estimate, this amount is retained as the site
              inspection / cancellation fee.
            </p>
          </div>
          <div className="bg-[#3D5A47] text-white rounded-2xl p-6 sm:p-7 flex items-start gap-4">
            <Leaf className="w-7 h-7 text-[#B5D43B] flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-2xl font-bold mb-2">Rebate / BSTC discount note</h2>
              <p className="text-white/80 text-sm leading-relaxed">
                The out-of-pocket prices shown assume the customer assigns the applicable BSTC (Battery Small-scale Technology
                Certificate) rebate to Supa Energy Hub. Final certificate value can vary with market conditions and eligibility,
                confirmed at the time of quoting.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-[#f8faf5] border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">FAQ</span>
          <h2 className="heading-2 text-[#3D5A47] mb-6">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="w-full" data-testid="deye-main-faq">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-left text-[#3D5A47] font-semibold text-sm hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-gray-600 text-sm">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-[#3D5A47] to-[#1a2820] text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-black mb-3">Find your Deye battery package</h2>
          <p className="text-white/80 mb-6">Free quote. We handle the BSTC rebate paperwork and install everything end-to-end.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/get-estimate?source=deye-main">
              <Button className="bg-[#B5D43B] hover:bg-[#a3c235] text-[#1a2820] gap-2 font-bold px-6 py-5 rounded-xl" data-testid="deye-main-cta-quote">
                <ClipboardList className="w-4 h-4" /> Get My Deye Battery Quote
              </Button>
            </Link>
            <a href="tel:0478453548">
              <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 gap-2 px-6 py-5 rounded-xl">
                <Phone className="w-4 h-4" /> Call 0478 453 548
              </Button>
            </a>
          </div>
        </div>
      </section>
    </main>
    <Footer />
  </div>
);

export default DeyePackagesPage;
